// src/components/GameDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AmazonSearch from './AmazonSearch';
import EbayWidget from './EbayWidget';
import VoteButtons from './VoteButtons';
import SEO from './SEO';
import { getGameSlug } from '../utils/slugHelper';

const GameDetails = ({ showLogin }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const showBackButton = () => {
    // If we have location state that indicates we came from a search
    if (location.state && location.state.from === '/search') {
      return true;
    }

    //recommendations page
    if (location.state && location.state.from === '/recommendations') {
      return true;
    }
    
    // Alternative: Check document.referrer to see if user came from our site's search page
    const referrer = document.referrer;
    const siteOrigin = window.location.origin;
    return referrer.startsWith(siteOrigin) && 
           (referrer === siteOrigin + '/' || referrer.includes('?q=') || referrer.includes('?players='));
  };

  const shouldShowBackButton = showBackButton();

  const handleGoBack = () => {
    if (location.state && location.state.from === '/search') {
      // Use either the search string or the fullSearchParams as backup
      const searchParams = location.state.search || 
                           (location.state.fullSearchParams ? `?${location.state.fullSearchParams}` : '');
      
      if (searchParams) {
        // If we have search parameters, navigate with them directly in the URL
        navigate(`/search${searchParams}`, { 
          state: { 
            from: 'game-details',
            preserveSearch: true,
            savedResults: location.state.savedResults || null
          }
        });
      } else {
        // Fall back to basic navigation with state
        navigate('/search', { 
          state: { 
            from: 'game-details',
            preserveSearch: true
          }
        });
      }
    } else if (location.state && location.state.from === '/recommendations') {
      // Navigate back to recommendations page
      navigate('/recommendations', {
        state: {
          from: 'game-details'
        }
      });
    } else {
      // Fallback to browser history if not from search
      navigate(-1);
    }
  };

  useEffect(() => {
    const fetchGame = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`/api/games/by-slug/${slug}`);
        
        if (!response.ok) {
          throw new Error('Game not found');
        }
        
        const data = await response.json();
        setGame(data);
        
        // Redirect to canonical URL if the slug doesn't match
        const canonicalSlug = getGameSlug(data);
        if (canonicalSlug !== slug) {
          navigate(`/game/${canonicalSlug}`, { replace: true });
        }

      } catch (err) {
        console.error('Error fetching game details:', err);
        setError('Game not found or unable to load details');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchGame();
    }
  }, [slug, navigate]);

  const handleVote = (gameId, voteValue, upvotes, downvotes) => {
    setGame(prev => ({
      ...prev,
      userVote: voteValue,
      upvotes,
      downvotes
    }));
  };
  

    
  const mapWeightToDifficulty = (weight) => {
    if (!weight) return '';
    
    if (weight === 'Light' || weight === 'Medium Light' || weight === 'Light Medium') {
      return 'Easy';
    } else if (weight === 'Medium') {
      return 'Medium';
    } else if (weight === 'Medium Heavy' || weight === 'Heavy') {
      return 'Hard';
    }
    
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-2">Loading game details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-200">
            <p>{error}</p>
            <button 
              onClick={handleGoBack}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-12">
            <p className="text-gray-400">Game not found</p>
            <button 
              onClick={handleGoBack}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
      <SEO 
        title={game.title}
        description={
          game.description_short || 
          `${game.title} - A board game for ${game.min_players === game.max_players ? game.min_players : `${game.min_players}-${game.max_players}`} players, taking about ${game.min_play_time === game.max_play_time ? `${game.min_play_time} minutes` : `${game.min_play_time}-${game.max_play_time} minutes`} to play.`
        }
        image={game.thumbnail ? `${window.location.origin}/media/${game.thumbnail}` : null}
        url={`${window.location.origin}/game/${getGameSlug(game)}`}
      />
      <div className="max-w-4xl mx-auto px-4">
        {/* Back button */}
        {shouldShowBackButton ? (
          <button 
            onClick={handleGoBack}
            className="mb-6 flex items-center text-blue-400 hover:text-blue-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {location.state && location.state.from === '/recommendations' 
              ? 'Back to recommendations'
              : 'Back to search results'}
          </button>
        ) : (
          <div className="mb-6"></div> // Empty div to maintain spacing
        )}
        
        {/* Game Title */}
        <h1 className="text-3xl font-bold mb-6 text-blue-400">{game.title}</h1>
        
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden mb-8">
          {/* Game image with overlay info */}
          <div className="w-full max-h-[300px] overflow-hidden bg-gray-700 flex items-center justify-center relative">
            <img 
              src={game.thumbnail ? `/media/${game.thumbnail}` : "/media/placeholder.gif"} 
              alt={`${game.title} board game`}
              className="w-full h-auto object-contain"
            />
            
            {/* Stats overlay at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-black bg-opacity-70 text-white px-3 py-2">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{game.min_players === game.max_players ? game.min_players : `${game.min_players}-${game.max_players}`}</span>
              </div>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{game.min_play_time === game.max_play_time ? `${game.min_play_time} min` : `${game.min_play_time}-${game.max_play_time} min`}</span>
              </div>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{mapWeightToDifficulty(game.weight)}</span>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {/* Vote buttons */}
            <div className="flex justify-end mb-4">
              <VoteButtons 
                game={game}
                onVote={handleVote}
                showLogin={showLogin}
              />
            </div>
            
            {/* Full description */}
            <div className="mb-6">
              <p className="text-gray-300 whitespace-pre-line">{game.description || game.description_short}</p>
            </div>
            
            {/* Game details */}
            <div className="mb-6">
              <div className="space-y-3">
                <div>
                  <span className="font-semibold block mb-1 text-gray-300">Themes:</span>
                  <div className="flex flex-wrap gap-2">
                    {game.themes?.map(theme => (
                      <span key={theme} className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-sm">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <span className="font-semibold block mb-1 text-gray-300">Mechanics:</span>
                  <div className="flex flex-wrap gap-2">
                    {game.mechanics?.map(mechanic => (
                      <span key={mechanic} className="bg-green-900 text-green-100 px-2 py-1 rounded-full text-sm">
                        {mechanic}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Amazon Search Section */}
            <AmazonSearch gameTitle={game.title} />
            
            {/* eBay Widget Section */}
            <EbayWidget gameTitle={game.title} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDetails;